import { Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { last } from "lodash-es";
import { PromptSuggestions } from "./co-pilot-prompt-suggestions";

import type { UUID } from "../../../../../utils/uuid";
import type { PromptSuggestion } from "./co-pilot-prompt-suggestions";
import type { CoPilotRequest } from "./use-co-pilot-types";
import type { StackProps } from "@mui/material";
import type { FC } from "react";

export const Requests: FC<
  {
    onRefine: (prompt: string) => void;
    onUseSuggestion?: (id?: UUID) => void;
    loading: boolean;
    requests: CoPilotRequest[];
    suggestions: PromptSuggestion[];
    mode: "replace" | "insert";
  } & StackProps
> = ({ onRefine, onUseSuggestion, loading, requests, ...props }) => (
  <Stack direction="column" spacing={3} divider={<Divider />} {...props}>
    {requests.map((request, i) => (
      <Request
        key={request.id}
        request={request}
        onUseSuggestion={onUseSuggestion}
        showPrompts={i === requests.length - 1}
        onRefine={onRefine}
        loading={loading}
        {...props}
      />
    ))}
  </Stack>
);

const Request: FC<{
  request: CoPilotRequest;
  onUseSuggestion?: (id: UUID) => void;
  showPrompts: boolean;
  onRefine: (prompt: string) => void;
  loading: boolean;
  suggestions: PromptSuggestion[];
  mode: "replace" | "insert";
}> = ({ suggestions, request, onUseSuggestion, showPrompts, loading, onRefine, mode }) => (
  <>
    <Typography variant="body2" sx={{ color: "text.discreet", fontStyle: "italic" }}>
      {request.prompt}
    </Typography>
    <TextField
      fullWidth
      multiline
      maxRows={20}
      value={last(request.responses)?.response || ""}
      disabled
    />
    <Box
      sx={{ display: "flex", alignItems: "center", flexDirection: "row-reverse", mt: 2, gap: 2 }}
    >
      <Button
        data-analytics-id={`lexical-co-pilot-use-suggestion-${mode}`}
        variant="outlined"
        onClick={() => onUseSuggestion?.(request.id)}
        sx={{ alignSelf: "right", mb: 0.5 }}
        title={
          mode === "replace"
            ? "Replace selected text with suggestion"
            : "Insert suggestion at cursor"
        }
      >
        {mode === "replace" && "Replace"}
        {mode === "insert" && "Insert"}
      </Button>
      {showPrompts && (
        <PromptSuggestions onUsePrompt={onRefine} loading={loading} suggestions={suggestions} />
      )}
    </Box>
  </>
);
